import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import useBreadcrumbs from '../../../utils/hooks/useBreadcrumbs';
import TitlePage from '../../../UI/TitlePage/TitlePage';
import Table from '../../../UI/Table/Table';
import Button from '../../../UI/Button/Button';
import Input from '../../../UI/Input/Input';
import SurveyAPI from '../../../services/api/SurveyService';
import { PaginationType } from '../../../Types/APITypes';
import { NotifyContext } from '../../../context/notifyContext';

interface SurveyResponse {
  id: number;
  booking_id: number;
  student: {
    id: number;
    name: string;
  };
  tutor: {
    id: number;
    name: string;
  };
  session_date: string;
  questions_answered: number;
  submitted_at: string | null;
  is_completed: boolean;
  created_at: string;
}

const ResponseList: React.FC = () => {
  const navigate = useNavigate();
  const notify = useContext(NotifyContext);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationType>({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    has_more_pages: false,
  });

  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    tutor_id: '',
    student_id: '',
    booking_id: '',
    completed: undefined as boolean | undefined,
  });

  useBreadcrumbs([
    { label: 'Tableau de bord', is_link: true, href: '/app/dashboard' },
    { label: 'Gestion des sondages', is_link: true, href: '/app/surveys' },
    { label: 'Réponses', is_link: false },
  ]);

  const fetchResponses = useCallback(async (paginationData: PaginationType) => {
    try {
      setLoading(true);
      const response: any = await SurveyAPI.getAllResponses({
        page: paginationData.current_page,
        per_page: paginationData.per_page,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        tutor_id: filters.tutor_id ? Number(filters.tutor_id) : undefined,
        student_id: filters.student_id ? Number(filters.student_id) : undefined,
        booking_id: filters.booking_id ? Number(filters.booking_id) : undefined,
        completed: filters.completed,
      });

      if (response && response.data) {
        setResponses(response.data.responses || []);
        setPagination(prev => ({
          ...prev,
          current_page: response.data.meta?.current_page || 1,
          total: response.data.meta?.total || 0,
          last_page: response.data.meta?.last_page || 1,
          has_more_pages: response.data.meta?.has_more_pages || false,
        }));
        setError(null);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des réponses');
      notify(err.message || 'Erreur lors du chargement des réponses', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchResponses(pagination);
  }, [pagination.current_page, pagination.per_page, filters]);

  const handleExport = async () => {
    try {
      const response = await SurveyAPI.exportResponses({
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        tutor_id: filters.tutor_id ? Number(filters.tutor_id) : undefined,
        student_id: filters.student_id ? Number(filters.student_id) : undefined,
      });

      if (response && response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `survey_responses_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        notify('Export réussi', 'success');
      }
    } catch (err: any) {
      notify(err.message || 'Erreur lors de l\'export', 'error');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns = [
    {
      Header: 'ID Réservation',
      accessor: 'booking_id',
    },
    {
      Header: 'Étudiant',
      accessor: 'student',
      Cell: ({ value }: any) => value?.name || '-',
    },
    {
      Header: 'Tuteur',
      accessor: 'tutor',
      Cell: ({ value }: any) => value?.name || '-',
    },
    {
      Header: 'Date de session',
      accessor: 'session_date',
      Cell: ({ value }: any) => formatDate(value),
    },
    {
      Header: 'Questions répondues',
      accessor: 'questions_answered',
    },
    {
      Header: 'Date de soumission',
      accessor: 'submitted_at',
      Cell: ({ value }: any) => (value ? formatDate(value) : '-'),
    },
    {
      Header: 'Statut',
      accessor: 'is_completed',
      Cell: ({ value }: any) => (
        <span
          className={`px-2 py-1 text-xs rounded-full ${
            value ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {value ? 'Complété' : 'En attente'}
        </span>
      ),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }: any) => (
        <button
          onClick={() => navigate(`/app/surveys/responses/${row.original.id}`)}
          className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Voir détails
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <TitlePage>
        <div className="flex justify-between items-center">
          <span>Réponses aux Sondages</span>
          <Button
            onClick={handleExport}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            Exporter en CSV
          </Button>
        </div>
      </TitlePage>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <h3 className="font-semibold text-gray-700">Filtres</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de début
            </label>
            <Input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de fin
            </label>
            <Input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID Tuteur
            </label>
            <Input
              type="number"
              value={filters.tutor_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, tutor_id: e.target.value }))}
              placeholder="Filtrer par tuteur"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID Étudiant
            </label>
            <Input
              type="number"
              value={filters.student_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, student_id: e.target.value }))}
              placeholder="Filtrer par étudiant"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID Réservation
            </label>
            <Input
              type="number"
              value={filters.booking_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, booking_id: e.target.value }))}
              placeholder="Filtrer par réservation"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statut
            </label>
            <select
              value={filters.completed === undefined ? '' : filters.completed.toString()}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  completed: e.target.value === '' ? undefined : e.target.value === 'true',
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous</option>
              <option value="true">Complétés</option>
              <option value="false">En attente</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={() =>
              setFilters({
                start_date: '',
                end_date: '',
                tutor_id: '',
                student_id: '',
                booking_id: '',
                completed: undefined,
              })
            }
            className="bg-gray-200 hover:bg-gray-300"
          >
            Réinitialiser les filtres
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow">
        <Table
          columns={columns as any}
          data={responses}
          fetchData={fetchResponses}
          loading={loading}
          pageCount={pagination.last_page || 1}
          total={pagination.total || 0}
        />
      </div>
    </div>
  );
};

export default ResponseList;
